/**
 * Bot Comparison Table Component
 *
 * Displays side-by-side comparison of bot metrics.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Cpu, Search, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { BotSessionBehavior, BotHealthScore } from '@/services/visibilityDiagnosisService';

interface BotComparisonTableProps {
  sessionBehavior: Record<string, BotSessionBehavior>;
  healthScores: Record<string, BotHealthScore>;
}

const gradeColors: Record<string, string> = {
  A: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  B: 'bg-lime-100 dark:bg-lime-900/30 text-lime-800 dark:text-lime-300',
  C: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  D: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  F: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
};

export function BotComparisonTable({ sessionBehavior, healthScores }: BotComparisonTableProps) {
  // Combine data and sort by total requests
  const bots = Object.keys(sessionBehavior)
    .map(botName => ({
      ...sessionBehavior[botName],
      healthScore: healthScores[botName],
    }))
    .sort((a, b) => b.total_requests - a.total_requests);

  // Separate AI bots and search bots
  const aiBots = bots.filter(b => b.is_ai_bot);
  const searchBots = bots.filter(b => !b.is_ai_bot);

  // Find reference bot (Googlebot or highest traffic search bot)
  const referenceBotData = searchBots.find(b => b.bot_name.toLowerCase().includes('googlebot')) || searchBots[0];

  const renderComparisonIcon = (value: number, reference: number) => {
    if (!reference || reference === 0) return null;
    const ratio = value / reference;
    if (ratio < 0.3) return <TrendingDown className="h-3 w-3 text-red-500" />;
    if (ratio < 0.7) return <Minus className="h-3 w-3 text-yellow-500" />;
    return <TrendingUp className="h-3 w-3 text-green-500" />;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const renderBotTable = (bots: typeof aiBots, title: string, Icon: typeof Cpu) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5" />
          {title}
          <Badge variant="outline" className="ml-2">{bots.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bot</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-right">Sessions</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-right">Avg URLs/Session</TableHead>
              <TableHead className="text-right">Median URLs</TableHead>
              <TableHead>Session Health</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bots.map(bot => (
              <TableRow key={bot.bot_name}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {bot.display_name}
                    {bot.is_ai_bot && (
                      <Badge variant="secondary" className="text-xs">AI</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {bot.healthScore ? (
                    <Badge className={gradeColors[bot.healthScore.grade]}>
                      {bot.healthScore.grade} ({bot.healthScore.total_score})
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {formatNumber(bot.total_sessions)}
                    {referenceBotData && renderComparisonIcon(bot.total_sessions, referenceBotData.total_sessions)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {formatNumber(bot.total_requests)}
                    {referenceBotData && renderComparisonIcon(bot.total_requests, referenceBotData.total_requests)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {bot.avg_urls_per_session.toFixed(1)}
                    {referenceBotData && renderComparisonIcon(bot.avg_urls_per_session, referenceBotData.avg_urls_per_session)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {bot.median_urls_per_session}
                    {referenceBotData && renderComparisonIcon(bot.median_urls_per_session, referenceBotData.median_urls_per_session)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        bot.diagnosis.severity === 'critical' ? 'destructive' :
                        bot.diagnosis.severity === 'warning' ? 'secondary' :
                        bot.diagnosis.severity === 'healthy' ? 'default' : 'outline'
                      }
                      className="text-xs"
                    >
                      {bot.diagnosis.severity}
                    </Badge>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {aiBots.length > 0 && renderBotTable(aiBots, 'AI Bot Comparison', Cpu)}
      {searchBots.length > 0 && renderBotTable(searchBots.slice(0, 10), 'Search Engine Comparison', Search)}
    </div>
  );
}
